import React, { useEffect, useRef, useState } from 'react';
import { BrandMark } from './BrandMark';

/**
 * Auto-scrolling image carousel for the Landing hero.
 * - 4-5 s per slide with cross-fade
 * - Pauses on hover / focus / when tab is hidden
 * - Respects prefers-reduced-motion
 * - Overlays the LUGGAGEFREEJOURNEY wordmark as a subtle watermark
 */
const SLIDES = [
  { src: '/hero/hero-0-wedding.jpg',   alt: 'Indian wedding family departing their villa; LuggageFreeJourney orange truck heading out through the gate with their luggage', caption: 'Weddings, made lighter' },
  { src: '/hero/hero-multi-bags.jpg',  alt: 'LuggageFreeJourney courier collecting four suitcases and bags from a smiling family at their doorstep, orange delivery van waiting on the road', caption: 'Whole-family pickup' },
  { src: '/hero/hero-6-students.jpg',  alt: 'College students in festive attire at an Indian railway station handing luggage to a LuggageFreeJourney courier, with a bus visible in the background', caption: 'Festive rush, sorted' },
  { src: '/hero/hero-2-airport.jpg',   alt: 'Traveler walking hands-free through a bright airport at dawn', caption: 'Travel light' },
  { src: '/hero/hero-3-gps.jpg',       alt: 'Overhead flat-lay of a phone showing a live GPS route beside an orange suitcase', caption: 'Live GPS tracking' },
  { src: '/hero/hero-4-otp.jpg',       alt: 'Close-up of an OTP-verified luggage handoff between courier and customer', caption: 'OTP-verified delivery' },
];

const INTERVAL_MS = 4200;

export const HeroCarousel = () => {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef(null);
  const reduceMotionRef = useRef(false);

  useEffect(() => {
    // prefers-reduced-motion: freeze on first slide
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    reduceMotionRef.current = mq.matches;
    const onChange = (e) => { reduceMotionRef.current = e.matches; };
    mq.addEventListener?.('change', onChange);

    const onVis = () => setPaused(document.hidden);
    document.addEventListener('visibilitychange', onVis);

    return () => {
      mq.removeEventListener?.('change', onChange);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, []);

  useEffect(() => {
    if (paused || reduceMotionRef.current) return undefined;
    timerRef.current = setInterval(() => {
      setActive((i) => (i + 1) % SLIDES.length);
    }, INTERVAL_MS);
    return () => clearInterval(timerRef.current);
  }, [paused]);

  // Preload the next image so cross-fade is smooth
  useEffect(() => {
    const next = (active + 1) % SLIDES.length;
    const img = new Image();
    img.src = SLIDES[next].src;
  }, [active]);

  return (
    <div
      data-testid="hero-carousel"
      className="relative w-full aspect-square max-w-[520px] mx-auto lg:ml-auto lg:mr-0"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
      tabIndex={-1}
    >
      {/* Decorative sharp-corner backdrop plate (offset shadow, on-brand) */}
      <div className="absolute inset-0 translate-x-3 translate-y-3 rounded-[28px] bg-[#1A1A1A]/90 hidden sm:block pointer-events-none" aria-hidden="true" />

      {/* Slide stack */}
      <div className="relative w-full h-full rounded-[28px] overflow-hidden border-2 border-[#1A1A1A] shadow-[8px_8px_0px_0px_#FF5A1F] bg-[#FFF7EE]">
        {SLIDES.map((s, i) => (
          <img
            key={s.src}
            src={s.src}
            alt={s.alt}
            loading={i === 0 ? 'eager' : 'lazy'}
            decoding="async"
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-[900ms] ease-in-out ${i === active ? 'opacity-100' : 'opacity-0'}`}
            data-testid={`hero-slide-${i}`}
          />
        ))}

        {/* Bottom gradient scrim + caption */}
        <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/70 via-black/20 to-transparent pointer-events-none" />
        <div className="absolute left-5 right-5 bottom-5 flex items-end justify-between gap-3 text-white pointer-events-none">
          <div>
            <div className="text-[10px] uppercase tracking-[0.35em] opacity-80">Slide {active + 1} / {SLIDES.length}</div>
            <div
              key={`cap-${active}`}
              className="text-lg sm:text-xl font-bold tracking-tight animate-[fadeSlideUp_600ms_ease-out]"
              data-testid="hero-slide-caption"
            >
              {SLIDES[active].caption}
            </div>
          </div>

          {/* Watermark wordmark on the image */}
          <div className="hidden sm:flex flex-col items-end gap-1 opacity-90">
            <BrandMark size={26} showText={false} />
            <div className="text-[8px] font-bold tracking-[0.28em] uppercase">LuggageFreeJourney</div>
          </div>
        </div>

        {/* Progress bar */}
        <div className="absolute left-0 right-0 top-0 h-1 bg-white/25">
          <div
            key={`bar-${active}-${paused}`}
            className="h-full bg-[#FF5A1F]"
            style={{
              width: '100%',
              transformOrigin: 'left',
              animation: paused || reduceMotionRef.current ? 'none' : `heroProgress ${INTERVAL_MS}ms linear forwards`,
            }}
          />
        </div>
      </div>

      {/* Dot indicators */}
      <div className="mt-4 flex items-center justify-center gap-2" data-testid="hero-carousel-dots">
        {SLIDES.map((_, i) => (
          <button
            key={i}
            type="button"
            aria-label={`Go to slide ${i + 1}`}
            data-testid={`hero-dot-${i}`}
            onClick={() => setActive(i)}
            className={`h-2 rounded-full transition-all ${i === active ? 'w-8 bg-[#FF5A1F]' : 'w-2 bg-[#1A1A1A]/25 hover:bg-[#1A1A1A]/45'}`}
          />
        ))}
      </div>

      {/* Local keyframes */}
      <style>{`
        @keyframes heroProgress {
          from { transform: scaleX(0); }
          to   { transform: scaleX(1); }
        }
        @keyframes fadeSlideUp {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default HeroCarousel;
